import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { ZeileFormular, type ZeileWerte } from './HandZeile';
import { KontoFeld } from './KontoFeld';
import { buText } from './kontenliste';
import {
  belegVorschlag,
  betragFeld,
  betragText,
  buErlaubt,
  gegenrichtung,
  ibanText,
  kontoInfo,
  rechnungenFuerTeil,
  rechnungSetzen,
  rechnungZeigen,
  STATUS_TEXT,
  teilBetraege,
  teilHinzu,
  teilWeg,
  type EntwurfTeil,
} from './logik';
import { StSlFeld } from './StSlFeld';
import type { KontoListe, ZeileSicht } from './types';
import { BelegFeld, RechnungFeld, useWahl } from './Wahl';

// Zeilen, bei denen die Seite den passenden Beleg schon einmal vorschlagen wollte (nicht noch einmal, wenn die
// Mitarbeiterin ihn wieder herausnimmt)
const belegVorgeschlagen = new Set<string>();

const STATUS_PILL: Record<ZeileSicht['status'], string> = {
  offen: 'p-bad',
  vorschlag: 'p-warn',
  bestaetigt: 'p-ok',
  verworfen: 'p-neu',
};

// Detail einer Bankzeile: Original, Herkunft, Befunde, Teilbuchungen bearbeiten (Gegenkonto, St-Sl, Betrag, Beleg,
// bei Geld rein die Rechnung), Bestätigen und die übrigen Knöpfe. Passt genau ein Beleg sicher, steht er als Entwurf
// schon drin (gespeichert erst mit dem Menschen). Enter springt Gegenkonto → St-Sl → (Betrag) → … → bestätigen; Esc
// bricht die Änderung ab.
export function ZeileDetail({
  zeile: z,
  kontoName,
  teile,
  geaendert,
  setTeile,
  liste,
  busy,
  fehler,
  onBestaetigen,
  onSpeichern,
  onAbbrechen,
  onZuruecknehmen,
  onVerwerfen,
  onKeinLohn,
  onBeleg,
  erster,
  letzter,
  onKorrigieren,
  onLoeschen,
}: {
  zeile: ZeileSicht;
  kontoName: string;
  teile: EntwurfTeil[];
  geaendert: boolean; // Entwurf von Hand (ein nur vorgeschlagener Beleg zählt nicht)
  // automatisch: nur der vorgeschlagene Beleg, nicht von Hand
  setTeile(t: EntwurfTeil[], automatisch?: boolean): void;
  liste: KontoListe | undefined;
  busy: boolean;
  fehler: string | null;
  onBestaetigen(): void;
  onSpeichern(): void;
  onAbbrechen(): void;
  onZuruecknehmen(): void;
  onVerwerfen(grund: 'doppelt' | 'vorgemerkt'): void;
  onKeinLohn(): void;
  onBeleg(id: string): void;
  erster: string; // erster und letzter Tag des Monats (JJJJ-MM-TT): Korrektur bleibt im Monat
  letzter: string;
  onKorrigieren(w: ZeileWerte): Promise<boolean>;
  onLoeschen(): void;
}) {
  const hid = useId();
  const ref = useRef<HTMLElement>(null);
  const [frage, setFrage] = useState(false);
  const [loeschFrage, setLoeschFrage] = useState(false);
  const [aendern, setAendern] = useState(false);
  const hand = z.quelle === 'hand';
  // Enter im letzten Feld bestätigt erst nach dem Neuzeichnen: dann mit dem gerade gewählten Konto (neueste Fassung)
  const bestaetigenRef = useRef(onBestaetigen);
  bestaetigenRef.current = onBestaetigen;
  const offen = z.status === 'offen' || z.status === 'vorschlag';
  const mehrere = teile.length > 1;
  const betraege = teilBetraege(teile, z.betragCents);
  const hart = z.befunde.filter((b) => b.art === 'hart');
  // Beträge im Editor ohne Vorzeichen, die Richtung steht in der Bezeichnung; Minus heißt Gegenrichtung
  const richtung = z.betragCents < 0 ? 'Ausgang' : 'Eingang';
  const hinweise = z.befunde.filter((b) => b.art === 'hinweis');
  const gegen = offen && betraege.some((c) => gegenrichtung(c, z.betragCents));
  // nur der Rest geht in die Gegenrichtung: Die anderen Teile ergeben mehr als den Bankbetrag (vielleicht vertippt)
  const restGegen =
    gegen &&
    mehrere &&
    betraege.slice(0, -1).every((c) => !gegenrichtung(c, z.betragCents)) &&
    gegenrichtung(betraege[betraege.length - 1], z.betragCents);

  // Auswahl für Beleg und Rechnung. Ein sicher passender Beleg kommt als Entwurf in den einzigen Teil (einmal je
  // Zeile), nur auf einer offenen Zeile ohne Buchung und ohne Entwurf von Hand (belegVorschlag): Was die Antwort
  // bis dahin schon getippt vorfindet, bleibt ein Entwurf von Hand.
  const { belege, rechnungen } = useWahl(z, offen);
  const neueste = useRef({ z, teile, setTeile, geaendert });
  neueste.current = { z, teile, setTeile, geaendert };
  useEffect(() => {
    if (!belege || !offen || belegVorgeschlagen.has(z.id)) return;
    belegVorgeschlagen.add(z.id);
    const { z: zeile, teile: t, setTeile: setzenAlle, geaendert: vonHand } = neueste.current;
    const b = belegVorschlag(zeile, t, belege, vonHand);
    if (b) setzenAlle([{ ...t[0]!, belegId: b.id, belegName: b.name }], true);
  }, [belege, offen, z.id]);

  // nächstes Feld mit Enter (nach dem Neuzeichnen, falls das St-Sl-Feld gerade weggefallen ist), am Ende bestätigen
  const weiter = () => {
    const von = document.activeElement;
    window.setTimeout(() => {
      const root = ref.current;
      if (!root) return;
      const felder = [...root.querySelectorAll<HTMLInputElement>('[data-enter]:not(:disabled)')];
      const feld = von instanceof HTMLElement ? von.dataset.feld : undefined;
      const i = felder.findIndex((f) => f.dataset.feld === feld);
      if (i < 0) return;
      const naechstes = felder[i + 1];
      if (naechstes) naechstes.focus();
      else bestaetigenRef.current();
    }, 0);
  };

  const setzen = (i: number, neu: Partial<EntwurfTeil>) => {
    setTeile(
      teile.map((t, j) => {
        if (j !== i) return t;
        const n = { ...t, ...neu };
        // anderes Gegenkonto: St-Sl leeren, wenn er dort nicht erlaubt ist (Automatikkonto, kein Vorsteuerkonto); die
        // Rechnung gehört zu ihrem Debitor und fällt weg
        if (neu.gegenkonto !== undefined) {
          const info = kontoInfo(liste, n.gegenkonto.trim());
          if (info.automatik || !buErlaubt(liste, n.gegenkonto.trim(), n.bu.trim())) n.bu = '';
          n.buchungstext = t.gegenkonto === n.gegenkonto ? t.buchungstext : undefined;
          if (t.gegenkonto.trim() !== n.gegenkonto.trim()) {
            n.rtRechnungId = null;
            n.rechnungNr = null;
          }
        }
        return n;
      }),
    );
  };

  const hinzu = () => {
    setTeile(teilHinzu(teile, z.betragCents));
    // Cursor in den Betrag des bisher letzten Teils: dort den Anteil tippen, der neue Teil bekommt den Rest
    const i = teile.length - 1;
    window.setTimeout(() => ref.current?.querySelector<HTMLInputElement>(`[data-feld="btr-${i}"]`)?.focus(), 0);
  };

  const taste = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (frage) setFrage(false);
      else onAbbrechen();
    }
  };

  const original = (i: number) => z.teile[i];

  return (
    <section className="zd" ref={ref} aria-labelledby={hid} onKeyDown={taste}>
      <div className="zd-h">
        <h2 id={hid}>
          {fmtDate(z.buchungstag)} · <span className={z.betragCents < 0 ? 'neg' : 'pos'}>{betragText(z.betragCents, { plus: true, euro: true })}</span>
        </h2>
        <span className="zd-marks">
          <span className={`pill ${STATUS_PILL[z.status]}`}>{STATUS_TEXT[z.status]}</span>
          {z.sicher && <span className="pill p-ok">sicher</span>}
          {z.quelleVorschlag === 'ki' && <span className="pill m-ki">KI</span>}
          {hand && <span className="pill p-info">von Hand</span>}
        </span>
      </div>

      <dl className="dl zd-orig">
        <dt>Name</dt>
        <dd>{z.gegenName || '–'}</dd>
        {z.gegenIban && (
          <>
            <dt>IBAN</dt>
            <dd className="num">{ibanText(z.gegenIban)}</dd>
          </>
        )}
        <dt>Zweck</dt>
        <dd className="zd-zweck">{z.verwendungszweck || '–'}</dd>
        {z.bankText && (
          <>
            <dt>Text der Bank</dt>
            <dd>{z.bankText}</dd>
          </>
        )}
        <dt>Datum</dt>
        <dd>
          {fmtDate(z.buchungstag)}
          {z.valuta && z.valuta !== z.buchungstag && <span className="muted"> · Valuta {fmtDate(z.valuta)}</span>}
        </dd>
        <dt>Konto</dt>
        <dd>
          <span className="num">{z.konto}</span> {kontoName}
        </dd>
      </dl>

      {z.aenderbar && offen && !aendern && (
        <p className="zd-aend">
          <button type="button" className="linkbtn" disabled={busy} onClick={() => setAendern(true)}>
            Datum oder Betrag ändern
          </button>
          {!hand && <span className="muted"> · falls die KI etwas falsch gelesen hat</span>}
        </p>
      )}
      {aendern && offen && (
        <ZeileFormular
          titel={hand ? 'Zeile von Hand ändern' : 'Lesung der KI korrigieren'}
          knopf="Übernehmen"
          start={{ datum: z.buchungstag, betragCents: z.betragCents, name: z.gegenName, zweck: z.verwendungszweck }}
          erster={erster}
          letzter={letzter}
          busy={busy}
          fehler={fehler}
          onSenden={(w) => void onKorrigieren(w).then((ok) => ok && setAendern(false))}
          onAbbrechen={() => setAendern(false)}
        />
      )}

      {!z.imAuszug && !hand && <p className="zd-bef hart">Diese Zeile steht nicht mehr im Kontoauszug. Bitte prüfen.</p>}
      {z.lohn !== 'nein' && (
        <div className="zd-lohn">
          <span>{z.lohn === 'ja' ? 'Als Lohnzahlung erkannt.' : 'Vielleicht Lohn (Sammelüberweisung).'}</span>
          {z.status !== 'bestaetigt' && z.status !== 'verworfen' && (
            <button type="button" className="btn sm" disabled={busy} onClick={onKeinLohn}>
              Keine Lohnzahlung
            </button>
          )}
        </div>
      )}
      {z.herkunft && (
        <p className="zd-herk">
          {z.herkunft}
          {z.kiTestmodus && <span className="muted"> (KI im Testbetrieb)</span>}
        </p>
      )}
      {hart.map((b) => (
        <p key={b.code} className="zd-bef hart">
          {b.satz}
        </p>
      ))}
      {hinweise.map((b) => (
        <p key={b.code} className="zd-bef hinweis">
          {b.satz}
        </p>
      ))}

      {offen ? (
        <div className="zd-ed" role="group" aria-label="Buchung">
          {teile.map((t, i) => {
            const o = original(i);
            const c = betraege[i];
            const letzter = i === teile.length - 1;
            const wo = mehrere ? ` (Teil ${i + 1})` : '';
            const fuerTeil = rechnungenFuerTeil(rechnungen, teile, i, z.betragCents);
            const mitRe = rechnungZeigen(z, t, fuerTeil.length);
            const re = t.rtRechnungId ? rechnungen.find((r) => r.id === t.rtRechnungId) : undefined;
            // Name zum Gegenkonto, solange die Kontenliste ihn nicht kennt (Debitor): vom Server oder aus der Rechnung
            const name =
              o && o.gegenkonto === t.gegenkonto.trim() ? o.gegenkontoName : re && re.debitor === t.gegenkonto.trim() ? re.kunde : null;
            return (
              <div key={t.key} className={`zt${mehrere ? ' multi' : ''}${mitRe ? ' mit-re' : ''}`}>
                <KontoFeld
                  value={t.gegenkonto}
                  onChange={(v) => setzen(i, { gegenkonto: v })}
                  liste={liste}
                  nameFallback={name}
                  label={`Gegenkonto${wo}`}
                  feld={`gk-${i}`}
                  disabled={busy}
                  onEnter={weiter}
                />
                <StSlFeld
                  value={t.bu}
                  onChange={(v) => setzen(i, { bu: v })}
                  gegenkonto={t.gegenkonto}
                  liste={liste}
                  label="St-Sl"
                  feld={`bu-${i}`}
                  disabled={busy}
                  onEnter={weiter}
                />
                <div className="zt-btr">
                  {mehrere && !letzter ? (
                    <>
                      <label htmlFor={`${hid}-b${i}`}>Betrag ({richtung})</label>
                      <input
                        id={`${hid}-b${i}`}
                        className="num"
                        value={t.betrag}
                        disabled={busy}
                        data-feld={`btr-${i}`}
                        data-enter=""
                        inputMode="decimal"
                        autoComplete="off"
                        enterKeyHint="next"
                        aria-invalid={c == null || undefined}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setzen(i, { betrag: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            weiter();
                          }
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <span className="zt-l">
                        {mehrere ? 'Rest' : 'Betrag'} ({richtung})
                      </span>
                      <span className={`num zt-v${c != null && c !== 0 ? '' : ' bad'}`}>
                        {/* wie in den Betragsfeldern: in Richtung der Bankzeile positiv */}
                        {c == null ? '–' : `${betragFeld(c, z.betragCents)} €`}
                      </span>
                    </>
                  )}
                </div>
                {mehrere && (
                  <button
                    type="button"
                    className="zt-weg"
                    disabled={busy}
                    aria-label={`Teil ${i + 1} entfernen`}
                    title="Teil entfernen"
                    onClick={() => setTeile(teilWeg(teile, t.key))}
                  >
                    ×
                  </button>
                )}
                <BelegFeld
                  z={z}
                  teil={t}
                  belege={belege}
                  vorschlag={!!t.belegId && t.belegId !== (o?.belegId ?? null)}
                  disabled={busy}
                  onWahl={(b) => setzen(i, { belegId: b?.id ?? null, belegName: b?.name ?? null })}
                  onAnsehen={onBeleg}
                />
                {mitRe && (
                  <RechnungFeld
                    teil={t}
                    rechnungen={fuerTeil}
                    disabled={busy}
                    onWahl={(r) => {
                      const neu = rechnungSetzen(teile, i, r, z.betragCents);
                      setTeile(neu);
                      // weniger offen als gezahlt: Der Rest ist ein neuer Teil, Cursor in sein Gegenkonto
                      if (neu.length > teile.length) {
                        window.setTimeout(() => ref.current?.querySelector<HTMLInputElement>(`[data-feld="gk-${neu.length - 1}"]`)?.focus(), 0);
                      }
                    }}
                  />
                )}
              </div>
            );
          })}
          {gegen && (
            <p className={`zt-minus${restGegen ? ' warn' : ''}`}>
              {restGegen
                ? 'Die anderen Teile ergeben mehr als den Bankbetrag, der Rest geht deshalb in die Gegenrichtung (Minus). Stimmt das?'
                : 'Minus heißt Gegenrichtung, z. B. einbehaltene Steuer bei Zinsen. Zusammen ergeben die Teile den Bankbetrag.'}
            </p>
          )}
          <button type="button" className="linkbtn zd-hinzu" disabled={busy} onClick={hinzu}>
            + Teil hinzufügen
          </button>
        </div>
      ) : (
        <div className="zd-fest" role="group" aria-label="Buchung">
          {z.teile.length === 0 && <p className="note">Keine Buchung.</p>}
          {z.teile.map((t) => (
            <div key={t.id} className="zf">
              <span>
                <b className="num">{t.gegenkonto}</b> {t.gegenkontoName ?? ''}
              </span>
              <span className="muted">{t.bu ? `St-Sl ${t.bu} ${t.buText ?? buText(liste, t.bu) ?? ''}` : 'ohne St-Sl'}</span>
              <span className={`num ${t.betragCents < 0 ? 'neg' : 'pos'}`}>{betragText(t.betragCents)}</span>
              {t.rtRechnungId && t.belegfeld1 && <span className="muted">Rechnung {t.belegfeld1}</span>}
              {t.belegId && (
                <button type="button" className="linkbtn" onClick={() => onBeleg(t.belegId!)}>
                  Beleg: {t.belegName ?? 'ansehen'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {fehler && !aendern && (
        <p className="zd-err" role="alert">
          {fehler}
        </p>
      )}

      {aendern ? null : loeschFrage ? (
        <div className="zd-frage" role="group" aria-label="Zeile löschen">
          <span>Diese Zeile von Hand löschen?</span>
          <span className="zd-act">
            <button type="button" className="btn sm" disabled={busy} autoFocus onClick={onLoeschen}>
              Löschen
            </button>
            <button type="button" className="btn sm" onClick={() => setLoeschFrage(false)}>
              Abbrechen
            </button>
          </span>
        </div>
      ) : frage ? (
        <div className="zd-frage" role="group" aria-label="Zeile verwerfen">
          <span>Warum verwerfen? Die Zeile wird dann nicht gebucht.</span>
          <span className="zd-act">
            <button type="button" className="btn sm" disabled={busy} autoFocus onClick={() => onVerwerfen('doppelt')}>
              Doppelt im Auszug
            </button>
            <button type="button" className="btn sm" disabled={busy} onClick={() => onVerwerfen('vorgemerkt')}>
              Nur vorgemerkt
            </button>
            <button type="button" className="btn sm" onClick={() => setFrage(false)}>
              Abbrechen
            </button>
          </span>
        </div>
      ) : (
        <div className="zd-act">
          {offen && (
            <>
              <button type="button" className="btn dark" disabled={busy} aria-keyshortcuts="Enter" onClick={onBestaetigen}>
                Bestätigen <span className="kbd">Enter</span>
              </button>
              {geaendert && (
                <>
                  <button type="button" className="btn" disabled={busy} onClick={onSpeichern}>
                    Nur speichern
                  </button>
                  <button type="button" className="btn" disabled={busy} aria-keyshortcuts="Escape" onClick={onAbbrechen}>
                    Abbrechen <span className="kbd">Esc</span>
                  </button>
                </>
              )}
              {hand ? (
                <button type="button" className="btn sm zd-weg" disabled={busy} onClick={() => setLoeschFrage(true)}>
                  Zeile löschen
                </button>
              ) : (
                <button type="button" className="btn sm zd-weg" disabled={busy} onClick={() => setFrage(true)}>
                  Zeile verwerfen
                </button>
              )}
            </>
          )}
          {!offen && (
            <button type="button" className="btn" disabled={busy} onClick={onZuruecknehmen}>
              Zurücknehmen
            </button>
          )}
        </div>
      )}
      {z.bestaetigtAm && !offen && (
        <p className="note">
          {z.status === 'verworfen' ? 'Verworfen' : 'Bestätigt'}
          {z.bestaetigtVon ? ` von ${z.bestaetigtVon}` : ''} am {fmtDateTime(z.bestaetigtAm)}
        </p>
      )}
    </section>
  );
}
